import React, { useMemo, useState } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import ModalComponent from '../../../components/modal/modal';
import ModalBodyComponent from '../../../components/modal/modalBodyComponent';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import FormSelect from '../../../components/dropdown/formSelect';
import AlertPopup from '../../../components/alert-popup';
import {
  useWealthlaneCustomerApi,
  useGetSandboxStateOptions,
  useSandboxLockState,
  useBoolean,
} from '../../../hooks';
import InitialValuesForm from '../../../constants/initialValues';
import { LockInfoBox, LockInfoIcon, LockInfoContent } from './sandbox.styled';

interface ChangeStatusModalProps {
  isModalOpen: boolean;
  closeModal: () => void;
  rowData: any;
  domId?: string;
}

const LOCKED_LABEL = 'Locked';

const CHANGE_STATUS_VALIDATION = Yup.object().shape({
    lockState: Yup.string().nullable().required('Please select a status'),
});

const ChangeStatusModal = ({
  isModalOpen,
  closeModal,
  rowData,
  domId,
}: ChangeStatusModalProps): JSX.Element => {
  const { handleRequest } = useWealthlaneCustomerApi();
  const { data: stateOptions, isLoading: isLoadingOptions } =
    useGetSandboxStateOptions(handleRequest);

  const { value: isConfirmOpen, setFalse: closeConfirm, setTrue: openConfirm } = useBoolean(false);

  const [selectedLockState, setSelectedLockState] = useState<number | null>(null);
  const [selectedLabel, setSelectedLabel] = useState<string>('');

  const lockedOptionValue = useMemo(
    () => stateOptions?.find((opt) => opt.label === LOCKED_LABEL)?.value,
    [stateOptions]
  );

  const currentTenantState = rowData?.tenantState as string | undefined;

  const initialFormValues = useMemo(() => {
    if (!stateOptions?.length) return InitialValuesForm.changeStatusForm;
    const matchingOption = stateOptions.find((opt) => opt.label === currentTenantState);
    return {
      lockState: matchingOption?.value ?? '',
    };
  }, [stateOptions, currentTenantState]);

  const handleCloseAll = () => {
    closeConfirm();
    closeModal();
  };

  const { mutateAsync: updateLockState, isLoading: isUpdating } = useSandboxLockState(
    handleRequest,
    handleCloseAll
  );

  const handleConfirmSubmit = async () => {
    if (selectedLockState == null) return;
    try {
      await updateLockState({
        tenantId: rowData?.tenantId,
        lockState: selectedLockState,
      });
    } catch (error) {}
  };

  const handleFormSubmit = (values: typeof InitialValuesForm.changeStatusForm) => {
    const lockStateValue = Number(values.lockState);
    const label = stateOptions?.find((opt) => opt.value === lockStateValue)?.label ?? '';
    setSelectedLockState(lockStateValue);
    setSelectedLabel(label);
    openConfirm();
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={initialFormValues}
        validationSchema={CHANGE_STATUS_VALIDATION}
        onSubmit={handleFormSubmit}>
        {({ touched, handleChange, errors, setFieldValue, values }) => {
          const isLockedSelected =
            values.lockState !== '' && Number(values.lockState) === Number(lockedOptionValue);

          return (
            <ModalComponent
              show={isModalOpen && !isConfirmOpen}
              closeModal={closeModal}
              size='sm'
              title='Change Status'
              id={domId}>
              <Form id='changeStatusForm'>
                <ModalBodyComponent id='changeStatusModalBody'>
                  <div className='mb-4'>
                    <FormSelect
                      type='text'
                      label='Select Status'
                      name='lockState'
                      id='lockState'
                      viewName='lockState'
                      errors={errors}
                      touched={touched}
                      options={stateOptions}
                      isLoading={isLoadingOptions}
                      values={values}
                      setFieldValue={setFieldValue}
                      placeholder='Select status...'
                      required
                    />
                  </div>
                  {isLockedSelected && (
                    <LockInfoBox id='lockInfoBox'>
                      <LockInfoIcon className='material-symbols-rounded'>info</LockInfoIcon>
                      <LockInfoContent>
                        <strong>Information:</strong>
                        <br />
                        When this instance is locked, the data will be preserved, you will not be
                        able to switch to this instance and make changes to the data. You can
                        however create a copy of this locked instance and switch to the copy for
                        full access. You can also unlock this instance at a later time if you wish
                        to.
                      </LockInfoContent>
                    </LockInfoBox>
                  )}
                </ModalBodyComponent>
                <ModalFooterComponent
                  modalStatus='Add'
                  loading={false}
                  onCloseModal={closeModal}
                  valueChanged={!values.lockState}
                  id='changeStatusModalFooter'
                  buttonTitle='Submit'
                />
              </Form>
            </ModalComponent>
          );
        }}
      </Formik>
      {isConfirmOpen && (
        <AlertPopup
          isShow={isConfirmOpen}
          closeModal={closeConfirm}
          customTitle={`You are about to change ${rowData?.name} instance status to ${selectedLabel}. Are you sure?`}
          handleSubmit={handleConfirmSubmit}
          loading={isUpdating}
          id='changeStatusConfirmModal'
        />
      )}
    </>
  );
};

export default ChangeStatusModal;
